import { useQuery } from "@apollo/client";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import SectionHeading from "@/components/SectionHeading";
import { useFlags } from "@/hooks/useFeatureFlags";
import { useParams } from "react-router-dom";
import { useMemo } from "react";
import { PERSON_TIMELINE_QUERY } from "../../timeline/queries";
import TimelineActivityHeatmap from "../../timeline/_components/TimelineActivityHeatmap";
import TimelineMilestones from "../../timeline/_components/TimelineMilestones";
import { buildTimelineActivityYears, buildTimelineMilestones, groupTimelineMilestones } from "../../timeline/utils";

export default function PersonTimelinePage() {
  const { pgaId } = useParams();
  const flags = useFlags();
  const timelineEnabled = !!flags["m360-timeline"];

  const { data, loading, error } = useQuery(PERSON_TIMELINE_QUERY, {
    variables: { pgaId: pgaId || "" },
    skip: !pgaId || !timelineEnabled
  });

  const milestones = useMemo(() => {
    if (!data?.person) return [];
    return buildTimelineMilestones(data.person);
  }, [data?.person]);

  const groups = useMemo(() => groupTimelineMilestones(milestones), [milestones]);
  const activityYears = useMemo(() => buildTimelineActivityYears(milestones), [milestones]);

  return (
    <div className="flex flex-col min-w-0">
      <SectionHeading
        heading="Timeline"
        body="View major milestones across this person's membership, PGM program, section affiliation, and career history."
      />
      {!timelineEnabled ? (
        <EmptyState
          iconName="timeline"
          title="Timeline Is Not Available"
          caption="This page is controlled by the m360-timeline feature flag."
        />
      ) : error ? (
        <ErrorBoundary variant="inline" title="Failed to Load Timeline" message={error.message} />
      ) : (
        <>
          <TimelineActivityHeatmap years={activityYears} loading={loading} />
          <TimelineMilestones groups={groups} loading={loading} />
        </>
      )}
    </div>
  );
}
